import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice } from '@hero/schema';
import { getInvoiceRoleForViewer } from '@hero/core';
import { ArrowRightIcon, HandCoinsIcon } from '@hero/web-icons';

export interface FinanceInvoiceCTAProps {
  invoice: Invoice;
  /** Org id of the viewer — used to determine seller-side eligibility. */
  viewerOrgId?: string;
  /** Click handler — typically navigates to the financing apply wizard. */
  onApply: (invoice: Invoice) => void;
  /** When `true`, renders the disabled-with-tooltip state. */
  disabled?: boolean;
  /**
   * Optional reason key (under `m-financing:invoice_drawer.financing_unavailable_reason.*`).
   * Used to explain why the CTA is hidden / disabled.
   */
  unavailableReason?: string;
  className?: string;
}

/**
 * Sticky CTA inviting the seller of an unpaid bilateral invoice to
 * factor it through the embedded-financing wizard.
 *
 * Rules:
 *  - **Buyers never see this** — only the seller can finance their
 *    own receivable.
 *  - **Paid / cancelled invoices** — hidden entirely.
 *  - **Already-financed invoices** — the consumer is expected to
 *    render `<ActiveFinancingBadge />` instead and pass
 *    `disabled` + `unavailableReason="already_financed"`.
 *
 * Mounts inside the shared `InvoiceDrawer`, the admin
 * `InvoiceView`, the `AccountPage` invoice list, and the
 * `ClientDrawer > Invoices` tab.
 */
export const FinanceInvoiceCTA: React.FC<FinanceInvoiceCTAProps> = ({
  invoice,
  viewerOrgId,
  onApply,
  disabled,
  unavailableReason,
  className,
}) => {
  const { t } = useTranslation(['m-financing']);

  const { role } = getInvoiceRoleForViewer(invoice, viewerOrgId);
  const isUnpaid = invoice.status !== 'paid' && invoice.status !== 'cancelled';
  const hasRemaining = (invoice.amount?.remaining ?? 0) > 0;

  if (role !== 'seller') return null;
  if (!isUnpaid || !hasRemaining) return null;
  // Once a `FinancingApplication` targets this invoice the back-link
  // is stamped (`invoice.financing`) — one-shot per invoice. The
  // consumer should render `<ActiveFinancingBadge />` instead of this
  // CTA; gating here keeps the CTA from rendering even when callers
  // forget the badge swap.
  if (invoice.financing) return null;

  const reasonLabel = unavailableReason
    ? t(`m-financing:invoice_drawer.financing_unavailable_reason.${unavailableReason}`, {
        defaultValue: '',
      })
    : '';

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-emerald-200/60 p-4 ${
        className ?? ''
      }`}
      style={{
        background:
          'linear-gradient(135deg, #ECFDF5, #D1FAE5, #A7F3D0, #D1FAE5, #ECFDF5)',
        backgroundSize: '200% 200%',
      }}>
      {/* Soft decorative orbs — same vocabulary as the
          `FinancingHero` so the in-drawer banner reads as a
          compact relative of the financing landing. */}
      <div className="pointer-events-none absolute -top-10 -end-10 size-32 rounded-full bg-emerald-300/20" />
      <div className="pointer-events-none absolute -bottom-8 -start-8 size-24 rounded-full bg-emerald-400/15" />

      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/60 bg-white/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 backdrop-blur-sm">
            <HandCoinsIcon className="size-3" />
            {t('m-financing:invoice_drawer.cta_finance.eyebrow', {
              defaultValue: 'Embedded financing',
            })}
          </div>
          <div className="mt-1.5 text-sm font-semibold text-emerald-950">
            {t('m-financing:invoice_drawer.cta_finance.title', {
              defaultValue: 'Finance this invoice',
            })}
          </div>
          <div className="text-xs text-emerald-900/80">
            {t('m-financing:invoice_drawer.cta_finance.description', {
              defaultValue:
                'Get paid now and let our financing partners settle on the due date.',
            })}
          </div>
          {reasonLabel ? (
            <div className="mt-1 text-xs text-amber-700">{reasonLabel}</div>
          ) : null}
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onApply(invoice)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white shadow-md hover:bg-emerald-800 disabled:opacity-50">
          {t('m-financing:invoice_drawer.cta_finance.button', {
            defaultValue: 'Finance it now',
          })}
          <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
        </button>
      </div>
    </div>
  );
};

export default FinanceInvoiceCTA;
